'use client';

import { useState, useRef, useEffect } from 'react';
import { ArrowRight, CheckCircle2, AlertCircle, ChevronDown, Check } from 'lucide-react';
import { getBookingUpstreamConfig } from '@/lib/bookingUpstream';

const SUBJECTS = [
  { value: 'order', label: 'I need help with my order' },
  { value: 'delivery', label: 'I have not received my ticket' },
  { value: 'correction', label: 'I need a correction on my ticket' },
  { value: 'visa', label: 'I have a visa or embassy question' },
  { value: 'pricing', label: 'I want to ask about pricing' },
  { value: 'other', label: 'Other' },
];

const EMPTY = { name: '', email: '', subject: '', orderRef: '', message: '' };

function CustomSelect({ value, onChange, onBlur, error, touched }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = SUBJECTS.find((s) => s.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); onBlur?.(); }}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm bg-white transition-colors text-left outline-none focus:ring-2 focus:ring-primary/10 ${
          touched && error
            ? 'border-red-300'
            : open
              ? 'border-primary/60'
              : 'border-slate-200 hover:border-primary/40'
        }`}
      >
        <span className={selected ? 'text-gray-800' : 'text-gray-700'}>
          {selected ? selected.label : 'Select a topic…'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-600 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
          {SUBJECTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => { onChange(s.value); setOpen(false); }}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm text-left transition-colors ${
                value === s.value
                  ? 'bg-tertiary text-primary font-semibold'
                  : 'text-gray-700 hover:bg-slate-50'
              }`}
            >
              {s.label}
              {value === s.value && <Check className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={2.5} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const inputCls = (error) =>
  `w-full rounded-xl border px-4 py-3 text-sm bg-white outline-none transition focus:ring-2 ${
    error
      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
      : 'border-slate-200 focus:border-primary/60 focus:ring-primary/10'
  }`;

function Field({ label, id, type = 'text', placeholder, value, onChange, onBlur, error }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={inputCls(error)}
      />
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="h-3 w-3 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
}

export default function ContactForm() {
  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle');
  const [submitError, setSubmitError] = useState('');

  const set = (key, val) => {
    setFields((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: '' }));
  };

  const blur = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const validate = () => {
    const e = {};
    if (!fields.name.trim()) e.name = 'Please enter your name.';
    if (!fields.email.trim()) e.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) e.email = 'Please enter a valid email.';
    if (!fields.subject) e.subject = 'Please select a topic.';
    if (!fields.message.trim()) e.message = 'Please describe your issue.';
    else if (fields.message.trim().length < 20) e.message = 'Please add a bit more detail (at least 20 characters).';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errs = validate();
    setErrors(errs);
    setTouched({ name: true, email: true, subject: true, message: true });
    if (Object.keys(errs).length) return;

    setStatus('submitting');
    try {
      const { base } = getBookingUpstreamConfig();
      const res = await fetch(`${base}/support`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(
          (typeof data?.error === 'string' && data.error) ||
          'Could not send your message. Please try again.',
        );
        setStatus('idle');
        return;
      }
      setStatus('success');
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
      setStatus('idle');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center text-center py-12 px-6">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-tertiary mb-5">
          <CheckCircle2 className="h-8 w-8 text-primary" strokeWidth={1.75} />
        </div>
        <h3 className="text-xl font-semibold text-primary mb-2">Message received</h3>
        <p className="text-sm text-gray-500 max-w-sm leading-relaxed">
          Thanks for reaching out. We typically respond within 2–4 hours.
          We&apos;ll reply to <strong className="text-gray-700">{fields.email}</strong>.
        </p>
        <button
          onClick={() => { setFields(EMPTY); setStatus('idle'); setTouched({}); setErrors({}); }}
          className="mt-6 text-sm font-semibold text-primary underline underline-offset-2 hover:no-underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Your name"
          id="name"
          placeholder="e.g. Ahmed Al Rashid"
          value={fields.name}
          onChange={(v) => set('name', v)}
          onBlur={() => blur('name')}
          error={touched.name ? errors.name : ''}
        />
        <Field
          label="Email address"
          id="email"
          type="email"
          placeholder="you@example.com"
          value={fields.email}
          onChange={(v) => set('email', v)}
          onBlur={() => blur('email')}
          error={touched.email ? errors.email : ''}
        />
      </div>

      <div>
        <label className="mb-1.5 block text-xs font-semibold text-slate-600">What can we help with?</label>
        <CustomSelect
          value={fields.subject}
          onChange={(v) => set('subject', v)}
          onBlur={() => blur('subject')}
          error={errors.subject}
          touched={touched.subject}
        />
        {touched.subject && errors.subject && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
            <AlertCircle className="h-3 w-3 shrink-0" /> {errors.subject}
          </p>
        )}
      </div>

      <Field
        label={<>Order reference <span className="text-gray-600 font-normal">(optional)</span></>}
        id="orderRef"
        placeholder="e.g. XK7P2Q — from your confirmation email"
        value={fields.orderRef}
        onChange={(v) => set('orderRef', v)}
      />

      <div>
        <label htmlFor="message" className="mb-1.5 block text-xs font-semibold text-slate-600">Your message</label>
        <textarea
          id="message"
          rows={5}
          placeholder="Describe your issue or question in detail…"
          value={fields.message}
          onChange={(e) => set('message', e.target.value)}
          onBlur={() => blur('message')}
          className={`w-full resize-none rounded-xl border px-4 py-3 text-sm bg-white outline-none transition focus:ring-2 ${
            touched.message && errors.message
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-200 focus:border-primary/60 focus:ring-primary/10'
          }`}
        />
        {touched.message && errors.message && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-red-500">
            <AlertCircle className="h-3 w-3 shrink-0" /> {errors.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 text-sm font-semibold text-white shadow-md shadow-primary/20 transition hover:brightness-110 disabled:opacity-60"
      >
        {status === 'submitting' ? (
          <><span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Sending…</>
        ) : (
          <>Send Message <ArrowRight className="h-4 w-4" strokeWidth={2.5} /></>
        )}
      </button>

      {submitError && (
        <p className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="h-3 w-3 shrink-0" /> {submitError}
        </p>
      )}
    </form>
  );
}
